// Team rules for CompanyOS (02 §team, 01 Team; 05 §Model `team`), on KPI Beast's containment
// engine (team-structure.ts, the `agency` engine with departments and standalone teams):
//   - a team may sit under a parent team, or stand alone with no parent (01: "Department" is the
//     UI label of a top-level team; a standalone team is a top-level team too);
//   - the team hierarchy is acyclic, and a sub-team's member seats are all members of its
//     parent (KPI Beast "Every member must belong to the parent department or team"), so by
//     induction of every ancestor;
//   - `composition` is derived from the member seats' active holders: no digital employee →
//     `human`, digital employees only → `digital`, both → `mixed` (a team with no holder is
//     `human`); a `digital` team needs a human owner seat.
// CompanyOS teams have explicit members (`team_member`): KPI Beast's `branch` mode and its
// `kind` column have no counterpart, so every team is checked as an explicit `team`. Its
// lead-must-be-a-member rule is not applied: 05 keeps `lead` and `member` as separate relations.
import { validateTeamStructure } from "./team-structure";

export type TeamComposition = "human" | "mixed" | "digital";

export type TeamNode = { id: string; parentTeamId: string | null; memberIds: readonly string[] };

/** Composition from the holders of the member seats. */
export function compositionOf(
  members: readonly { person: boolean; digital: boolean }[],
): TeamComposition {
  const person = members.some((m) => m.person);
  const digital = members.some((m) => m.digital);
  return digital ? (person ? "mixed" : "digital") : "human";
}

/**
 * Why `team` (as it would be after a write) breaks the hierarchy among `teams`, or null:
 * a cycle (checked first: KPI Beast's walk would report a cycle through a parent with other
 * members as a membership error), a missing parent, or a member outside its parent. `teams`
 * may include `team`'s old state; it is replaced.
 */
export function hierarchyError(
  team: TeamNode,
  teams: readonly TeamNode[],
): { code: "cycle" | "parent"; message: string } | null {
  const all = [...teams.filter((t) => t.id !== team.id), team];
  if (team.parentTeamId !== null && isWithin(team.parentTeamId, team.id, all))
    return { code: "cycle", message: "A team hierarchy cannot contain a cycle" };
  const shape = (t: TeamNode) => ({
    id: t.id,
    kind: "team",
    parentTeamId: t.parentTeamId,
    leadSeatId: null,
    membershipMode: "explicit",
    memberIds: [...t.memberIds],
  });
  const error = validateTeamStructure(shape(team), all.map(shape));
  if (error === null) return null;
  return { code: /cycle/.test(error) ? "cycle" : "parent", message: error };
}

/** The ids of the teams under `teamId` (children, grandchildren…), cycle-safe. */
export function descendantsOf(teamId: string, teams: readonly TeamNode[]): string[] {
  const out: string[] = [];
  const seen = new Set([teamId]);
  const pending = [teamId];
  while (pending.length > 0) {
    const id = pending.pop() as string;
    for (const t of teams)
      if (t.parentTeamId === id && !seen.has(t.id)) {
        seen.add(t.id);
        out.push(t.id);
        pending.push(t.id);
      }
  }
  return out;
}

/** Whether `teamId`'s ancestors include `candidate` (or it is `candidate`), cycle-safe. */
export function isWithin(teamId: string, candidate: string, teams: readonly TeamNode[]): boolean {
  const byId = new Map(teams.map((t) => [t.id, t]));
  const seen = new Set<string>();
  let cur: string | null = teamId;
  while (cur !== null && !seen.has(cur)) {
    if (cur === candidate) return true;
    seen.add(cur);
    cur = byId.get(cur)?.parentTeamId ?? null;
  }
  return false;
}
